import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import useDropdownPlacement from '../hooks/useDropdownPlacement';
import { markDialogDirty, focusPastElement } from '../hooks/useDialog';

// The calendar is a fixed-size grid, so it can only ever flip — never shrink.
const CALENDAR_H = 380;

/**
 * Modern date picker with month + year navigation.
 *
 * Props:
 *   value:        ISO date string ("YYYY-MM-DD") or '' / null
 *   onChange:     (newValue: string) => void  — emits "YYYY-MM-DD" or '' when cleared
 *   min:          optional ISO date string (dates before this are disabled)
 *   max:          optional ISO date string (dates after this are disabled)
 *   placeholder:  optional input placeholder text
 *   disabled:     optional boolean
 *   error:        optional boolean — adds red border styling
 *   id, name:     optional, forwarded for form integration
 *   format:       'short' (Apr 12, 2026) | 'long' (12 April 2026) — display format
 *
 * Notes:
 *  - We render dates in the user's locale for *display*; the value passed via onChange is
 *    always ISO "YYYY-MM-DD" so it's safe to send to the backend without timezone issues.
 *  - Year picker (click the year header) lets you jump decades quickly — important for DOB.
 *
 * Keyboard:
 *  - On the field, Enter, Space or ArrowDown opens the calendar with the cursor in a date box, and
 *    Delete or Backspace clears the date.
 *  - In the date box, type the date day first (12/03/1985, 12-3-85, 12031985, or t for today) and
 *    press Enter or Tab. ArrowDown moves into the calendar.
 *  - In the calendar, the arrow keys move by a day or a week, Home and End go to the start or end
 *    of the week, PageUp and PageDown change the month (with Shift, the year), and Enter picks.
 *    Shift+Tab goes back to the date box and Tab moves on to the next field.
 *  - Escape closes without changing anything. Closing from the keyboard returns focus to the field.
 * It used to be mouse-only: nothing could be typed, the arrow keys did nothing, focus fell to the
 * top of the page after a pick, and the clear button could not be reached.
 */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Parse ISO "YYYY-MM-DD" without timezone shift (avoids the new Date() midnight UTC pitfall)
const parseISODate = (iso) => {
  if (!iso) return null;
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

const formatISODate = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const sameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const formatDisplay = (d, format = 'short') => {
  if (!d) return '';
  if (format === 'long') {
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const pad2 = (n) => String(n).padStart(2, '0');
// Day first, the way dates are written here.
const formatTyped = (d) => (d ? `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}` : '');
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
// Keeps the day where the target month has it, else its last day (31 Jan + 1 month = 28 or 29 Feb).
const addMonths = (d, n) => {
  const lastDay = new Date(d.getFullYear(), d.getMonth() + n + 1, 0).getDate();
  return new Date(d.getFullYear(), d.getMonth() + n, Math.min(d.getDate(), lastDay));
};

/**
 * Reads a typed date, day first. The parts may be separated by / - . or spaces, or run together
 * (12031985, 120385); a year-first ISO date is accepted too, and "t" or "today" means today. A
 * two-digit year takes the century that keeps the date inside min and max, else the one nearer
 * today. Returns a local-midnight Date, or null for anything that is not a real date (31/02/2024).
 */
export const parseTypedDate = (text, { min = null, max = null, today = new Date() } = {}) => {
  const s = String(text || '').trim();
  if (!s) return null;
  if (/^t(oday)?$/i.test(s)) return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  let day;
  let month;
  let yearText;
  const isoLike = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (isoLike) {
    [, yearText, month, day] = isoLike;
  } else {
    const m = s.match(/^(\d{1,2})[\s/.-]+(\d{1,2})[\s/.-]+(\d{4}|\d{2})$/) || s.match(/^(\d{2})(\d{2})(\d{4}|\d{2})$/);
    if (!m) return null;
    [, day, month, yearText] = m;
  }
  const build = (year) => {
    const d = new Date(year, Number(month) - 1, Number(day));
    return d.getFullYear() === year && d.getMonth() === Number(month) - 1 && d.getDate() === Number(day) ? d : null;
  };
  if (yearText.length === 4) return build(Number(yearText));
  const candidates = [2000 + Number(yearText), 1900 + Number(yearText)].map(build).filter(Boolean);
  if (candidates.length === 0) return null;
  const inRange = candidates.filter((d) => (!min || d >= min) && (!max || d <= max));
  const pool = inRange.length > 0 ? inRange : candidates;
  const now = today.getTime();
  return pool.reduce((best, d) => (Math.abs(d - now) < Math.abs(best - now) ? d : best));
};

const DatePicker = ({
  value,
  onChange,
  min,
  max,
  placeholder = 'Select date',
  disabled = false,
  error = false,
  id,
  name,
  format = 'short',
  className = '',
  // 'sm' (default — matches patient form's `px-3 py-2 border rounded-lg`) or 'lg'
  // (matches the user-form input style: `p-3.5 border-2 rounded-xl`). Lets forms with
  // heavier input styling sit a DatePicker next to a regular input without mismatched
  // heights.
  size = 'sm',
}) => {
  const selectedDate = useMemo(() => parseISODate(value), [value]);
  const minDate = useMemo(() => parseISODate(min), [min]);
  const maxDate = useMemo(() => parseISODate(max), [max]);
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const [open, setOpen] = useState(false);
  const [view, setView] = useState('days'); // 'days' | 'months' | 'years'
  // viewMonth is the month currently shown in the calendar (independent of selectedDate)
  const [viewMonth, setViewMonth] = useState(() => {
    if (selectedDate) return new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  // The date box at the top of the calendar, and why what is in it cannot be used, if it cannot.
  const [typed, setTyped] = useState('');
  const [typedError, setTypedError] = useState('');
  // The day the keyboard is on in the calendar; null until the arrow keys are used there.
  const [focusDate, setFocusDate] = useState(null);

  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const gridRef = useRef(null);
  // Where focus goes once the next render is on screen: 'input', or a day as "YYYY-MM-DD".
  const pendingFocus = useRef(null);

  // When `value` changes externally, sync the view to that month. When the value is
  // cleared (form reset / Clear Form button), snap back to today's month so reopening
  // the calendar doesn't show the previously-selected month from a stale picker state.
  useEffect(() => {
    if (selectedDate) {
      setViewMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
    } else {
      setViewMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    }
  }, [selectedDate, today]);

  const resetPopover = () => {
    setOpen(false);
    setView('days');
    setTypedError('');
    setFocusDate(null);
  };

  const closePicker = (refocus) => {
    resetPopover();
    if (refocus) triggerRef.current?.focus();
  };

  const openPicker = (fromKeyboard) => {
    setTyped(formatTyped(selectedDate));
    setTypedError('');
    setFocusDate(null);
    setView('days');
    setOpen(true);
    pendingFocus.current = fromKeyboard ? 'input' : null;
  };

  // Close on outside click, and on Escape. Escape is taken in the capture phase and stopped, so it
  // closes the calendar and not a dialog the field sits in.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) resetPopover();
    };
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      resetPopover();
      triggerRef.current?.focus();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  // Runs after every render: a focus move asked for before the element it targets was on screen.
  useEffect(() => {
    const want = pendingFocus.current;
    if (!open || !want) return;
    if (want === 'input') {
      if (!inputRef.current) return;
      inputRef.current.focus();
      inputRef.current.select();
    } else {
      const el = gridRef.current?.querySelector(`[data-date="${want}"]`);
      if (!el) return;
      el.focus();
    }
    pendingFocus.current = null;
  });

  const isDisabledDay = (d) => {
    if (minDate && d < minDate) return true;
    if (maxDate && d > maxDate) return true;
    return false;
  };

  // Navigation is view-aware:
  //   days   → step by month
  //   months → step by year
  //   years  → jump by 12-year window
  //
  // canGoPrev / canGoNext gate the chevrons against the min/max bounds so the user
  // can't paginate to a window where every cell is disabled (e.g. DOB has max=today,
  // so navigating to a future month would just show a grid of greyed-out cells —
  // confusing and pointless).
  const canGoPrev = (() => {
    if (!minDate) return true;
    if (view === 'years') {
      // Previous 12-year window starts at viewYear - 17 (centre - 5, then minus 12).
      return viewMonth.getFullYear() - 17 >= minDate.getFullYear() - 11;
    }
    if (view === 'months') {
      return viewMonth.getFullYear() - 1 >= minDate.getFullYear();
    }
    // days: previous month last day must be >= minDate
    const prevMonthEnd = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 0);
    return prevMonthEnd >= minDate;
  })();
  const canGoNext = (() => {
    if (!maxDate) return true;
    if (view === 'years') {
      // Next 12-year window starts at viewYear + 7 (centre + 6, then plus 1 → years 7..18).
      return viewMonth.getFullYear() + 7 <= maxDate.getFullYear();
    }
    if (view === 'months') {
      return viewMonth.getFullYear() + 1 <= maxDate.getFullYear();
    }
    // days: next month first day must be <= maxDate
    const nextMonthStart = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1);
    return nextMonthStart <= maxDate;
  })();

  const goPrev = () => {
    if (!canGoPrev) return;
    if (view === 'years') {
      setViewMonth(new Date(viewMonth.getFullYear() - 12, viewMonth.getMonth(), 1));
    } else if (view === 'months') {
      setViewMonth(new Date(viewMonth.getFullYear() - 1, viewMonth.getMonth(), 1));
    } else {
      setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
    }
  };
  const goNext = () => {
    if (!canGoNext) return;
    if (view === 'years') {
      setViewMonth(new Date(viewMonth.getFullYear() + 12, viewMonth.getMonth(), 1));
    } else if (view === 'months') {
      setViewMonth(new Date(viewMonth.getFullYear() + 1, viewMonth.getMonth(), 1));
    } else {
      setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
    }
  };

  // Every change goes through here, so a dialog the field sits in knows its form was changed (a
  // pick fires no input event for it to notice).
  const emit = (iso) => {
    if (iso === (value || '')) return;
    markDialogDirty(wrapperRef.current);
    onChange(iso);
  };

  const selectDay = (d, fromKeyboard = false) => {
    if (isDisabledDay(d)) return;
    emit(formatISODate(d));
    closePicker(fromKeyboard);
  };

  const clearDate = (e) => {
    e.stopPropagation();
    emit('');
  };

  // Puts the keyboard on a day, bringing its month into view first when it is not showing.
  const moveFocus = (d) => {
    let target = d;
    if (minDate && target < minDate) target = minDate;
    if (maxDate && target > maxDate) target = maxDate;
    const iso = formatISODate(target);
    const monthChanges =
      target.getFullYear() !== viewMonth.getFullYear() || target.getMonth() !== viewMonth.getMonth();
    setFocusDate(target);
    setView('days');
    if (monthChanges) setViewMonth(new Date(target.getFullYear(), target.getMonth(), 1));
    pendingFocus.current = iso;
    if (!monthChanges && view === 'days') {
      const el = gridRef.current?.querySelector(`[data-date="${iso}"]`);
      if (el) {
        el.focus();
        pendingFocus.current = null;
      }
    }
  };

  const readTyped = () => {
    const text = typed.trim();
    if (!text) return { empty: true };
    const d = parseTypedDate(text, { min: minDate, max: maxDate, today });
    if (!d) return { problem: 'Enter the date as DD/MM/YYYY' };
    if (minDate && d < minDate) return { problem: `Pick a date on or after ${formatTyped(minDate)}` };
    if (maxDate && d > maxDate) return { problem: `Pick a date on or before ${formatTyped(maxDate)}` };
    return { date: d };
  };

  // Applies the date box. Returns false, with the reason shown under the box, when it cannot.
  const applyTyped = () => {
    const { empty, problem, date } = readTyped();
    if (problem) {
      setTypedError(problem);
      return false;
    }
    emit(empty ? '' : formatISODate(date));
    return true;
  };

  const onTypedChange = (e) => {
    const text = e.target.value;
    setTyped(text);
    setTypedError('');
    // Show the month of a date as soon as the text reads as one, so the calendar confirms it.
    const d = parseTypedDate(text, { min: minDate, max: maxDate, today });
    if (d) setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1));
  };

  const onTypedKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (applyTyped()) closePicker(true);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      if (!applyTyped()) return;
      if (e.shiftKey) {
        closePicker(true);
      } else {
        resetPopover();
        focusPastElement(wrapperRef.current);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveFocus(readTyped().date || selectedDate || today);
    }
  };

  const onGridKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      if (e.shiftKey) {
        inputRef.current?.focus();
      } else {
        resetPopover();
        focusPastElement(wrapperRef.current);
      }
      return;
    }
    const from = parseISODate(e.target.getAttribute('data-date')) || focusDate || selectedDate || today;
    const moves = {
      ArrowLeft: () => addDays(from, -1),
      ArrowRight: () => addDays(from, 1),
      ArrowUp: () => addDays(from, -7),
      ArrowDown: () => addDays(from, 7),
      Home: () => addDays(from, -from.getDay()),
      End: () => addDays(from, 6 - from.getDay()),
      PageUp: () => addMonths(from, e.shiftKey ? -12 : -1),
      PageDown: () => addMonths(from, e.shiftKey ? 12 : 1),
    };
    if (!moves[e.key]) return;
    e.preventDefault();
    moveFocus(moves[e.key]());
  };

  const onTriggerKeyDown = (e) => {
    if (disabled || open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      openPicker(true);
    } else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedDate) {
      e.preventDefault();
      emit('');
    }
  };

  // Build the day grid for the current viewMonth (6 rows × 7 cols, with leading/trailing days from adjacent months)
  const days = useMemo(() => {
    const firstOfMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const startDayOfWeek = firstOfMonth.getDay(); // 0=Sun
    const startDate = new Date(firstOfMonth);
    startDate.setDate(startDate.getDate() - startDayOfWeek);
    const result = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      d.setHours(0, 0, 0, 0);
      result.push(d);
    }
    return result;
  }, [viewMonth]);

  const goToday = () => {
    setViewMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    if (!isDisabledDay(today)) selectDay(today);
  };

  // Year picker: shows a 12-year grid (3×4) around the current view year
  const yearGrid = useMemo(() => {
    const center = viewMonth.getFullYear();
    const start = center - 5;
    return Array.from({ length: 12 }, (_, i) => start + i);
  }, [viewMonth]);

  const selectYear = (year) => {
    setViewMonth(new Date(year, viewMonth.getMonth(), 1));
    setView('months'); // go to month picker, not straight to days
  };

  const selectMonth = (monthIndex) => {
    setViewMonth(new Date(viewMonth.getFullYear(), monthIndex, 1));
    setView('days');
  };

  const displayValue = selectedDate ? formatDisplay(selectedDate, format) : '';
  // The date the box currently reads as, marked in the calendar until it is applied.
  const previewDate = open && typed ? parseTypedDate(typed, { min: minDate, max: maxDate, today }) : null;

  const { dropUp } = useDropdownPlacement(open, wrapperRef, { wantHeight: CALENDAR_H });

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {/* Trigger input */}
      <button
        ref={triggerRef}
        type="button"
        id={id}
        name={name}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={(e) => {
          if (disabled) return;
          // A click made with Enter or Space has no pointer behind it (detail is 0); only then does
          // the cursor go into the date box, so a tap on a phone does not raise its keyboard.
          if (open) closePicker(false);
          else openPicker(e.detail === 0);
        }}
        onKeyDown={onTriggerKeyDown}
        className={`w-full flex items-center justify-between text-left transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
          size === 'lg' ? 'p-3.5 border-2 rounded-xl' : 'px-3 py-2 border rounded-lg'
        } ${
          error
            ? 'border-red-500 focus:border-red-500'
            : open
            ? 'border-emerald-500'
            : 'border-gray-300 hover:border-gray-400 focus:border-emerald-500'
        } ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'bg-white'}`}
      >
        <span className={`whitespace-nowrap overflow-hidden text-ellipsis min-w-0 ${displayValue ? 'text-gray-900' : 'text-gray-400'}`}>
          {displayValue || placeholder}
        </span>
        {/* One icon at a time: the calendar invites a pick while the field is empty, the clear
            button replaces it once a date is in. Showing both squeezed the date itself out of
            narrow fields (a receipt grid cell showed "Aug..." instead of the expiry), and the
            calendar hint is redundant on a field that visibly holds a date. From the keyboard,
            Delete or Backspace on the field does what the clear button does. */}
        <div className="flex items-center flex-shrink-0">
          {displayValue && !disabled ? (
            <span
              role="button"
              tabIndex={-1}
              onClick={clearDate}
              className="p-0.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
              title="Clear date"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          ) : (
            <Calendar className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </button>

      {/* Popover */}
      {open && (
        <div
          role="dialog"
          aria-label="Choose a date"
          className={`absolute left-0 z-50 bg-white rounded-xl shadow-2xl border border-gray-200 p-3 w-72 select-none
            ${dropUp ? 'bottom-full mb-2' : 'top-full mt-2'}`}
        >
          {/* Typed date. Typing here is not yet a change to the form, hence data-no-dirty. */}
          <div className="mb-2">
            <input
              ref={inputRef}
              type="text"
              value={typed}
              onChange={onTypedChange}
              onKeyDown={onTypedKeyDown}
              placeholder="DD/MM/YYYY"
              aria-label="Type a date, day first"
              aria-invalid={typedError ? true : undefined}
              autoComplete="off"
              data-no-dirty
              className={`w-full px-3 py-1.5 text-sm border rounded-lg select-text focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                typedError ? 'border-red-400' : 'border-gray-300'
              }`}
            />
            {typedError && <p className="mt-1 text-[11px] text-red-600">{typedError}</p>}
          </div>

          {/* Header — click title to go up: days→months→years. Chevrons step within the current view.
              Not tab stops: from the keyboard, PageUp and PageDown do what the chevrons do. */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              tabIndex={-1}
              onClick={goPrev}
              disabled={!canGoPrev}
              className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              aria-label={view === 'years' ? 'Previous 12 years' : view === 'months' ? 'Previous year' : 'Previous month'}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setView(view === 'days' ? 'months' : view === 'months' ? 'years' : 'months')}
              className="px-3 py-1 text-sm font-semibold text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {view === 'years'
                ? `${viewMonth.getFullYear() - 5} – ${viewMonth.getFullYear() + 6}`
                : view === 'months'
                ? `${viewMonth.getFullYear()}`
                : `${MONTH_NAMES[viewMonth.getMonth()]} ${viewMonth.getFullYear()}`}
            </button>
            <button
              type="button"
              tabIndex={-1}
              onClick={goNext}
              disabled={!canGoNext}
              className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              aria-label={view === 'years' ? 'Next 12 years' : view === 'months' ? 'Next year' : 'Next month'}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {view === 'days' ? (
            <>
              {/* Weekday labels */}
              <div className="grid grid-cols-7 mb-1">
                {WEEKDAYS.map((wd) => (
                  <div key={wd} className="text-center text-[10px] font-semibold text-gray-400 uppercase tracking-wide py-1">
                    {wd}
                  </div>
                ))}
              </div>

              {/* Day grid */}
              <div ref={gridRef} className="grid grid-cols-7 gap-0.5" onKeyDown={onGridKeyDown}>
                {days.map((d) => {
                  const iso = formatISODate(d);
                  const inMonth = d.getMonth() === viewMonth.getMonth();
                  const isSelected = sameDay(d, selectedDate);
                  const isPreview = !isSelected && sameDay(d, previewDate);
                  const isToday = sameDay(d, today);
                  const isDisabled = isDisabledDay(d);
                  return (
                    <button
                      key={iso}
                      type="button"
                      data-date={iso}
                      tabIndex={sameDay(d, focusDate) ? 0 : -1}
                      onClick={(e) => selectDay(d, e.detail === 0)}
                      disabled={isDisabled}
                      aria-label={d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                      aria-pressed={isSelected}
                      className={`h-8 w-8 mx-auto flex items-center justify-center text-sm rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-1 ${
                        isDisabled
                          ? 'text-gray-200 cursor-not-allowed'
                          : isSelected
                          ? 'bg-emerald-600 text-white font-semibold shadow-md hover:bg-emerald-700'
                          : isPreview
                          ? 'ring-2 ring-emerald-500 text-emerald-700 font-semibold'
                          : isToday
                          ? 'ring-1 ring-emerald-400 text-emerald-700 font-semibold hover:bg-emerald-50'
                          : inMonth
                          ? 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                          : 'text-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={goToday}
                  disabled={isDisabledDay(today)}
                  className="text-xs font-medium text-emerald-600 hover:text-emerald-700 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Today
                </button>
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => closePicker(false)}
                  className="text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </>
          ) : view === 'months' ? (
            // Month grid — 3 columns × 4 rows. Disable months entirely past max /
            // before min so e.g. with max=today the May 2026 year view only enables
            // Jan-May.
            <div className="grid grid-cols-3 gap-2 py-2">
              {MONTH_NAMES.map((monthName, idx) => {
                const isCurrent = idx === viewMonth.getMonth();
                const monthStart = new Date(viewMonth.getFullYear(), idx, 1);
                const monthEnd = new Date(viewMonth.getFullYear(), idx + 1, 0);
                const allDisabled =
                  (minDate && monthEnd < minDate) || (maxDate && monthStart > maxDate);
                return (
                  <button
                    key={idx}
                    type="button"
                    tabIndex={-1}
                    onClick={() => selectMonth(idx)}
                    disabled={allDisabled}
                    className={`py-2.5 text-sm rounded-lg transition-colors ${
                      allDisabled
                        ? 'text-gray-300 cursor-not-allowed'
                        : isCurrent
                        ? 'bg-emerald-600 text-white font-semibold'
                        : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                    }`}
                  >
                    {monthName.slice(0, 3)}
                  </button>
                );
              })}
            </div>
          ) : (
            // Year grid — 3 columns × 4 rows
            <div className="grid grid-cols-3 gap-2 py-2">
              {yearGrid.map((year) => {
                const isCurrent = year === viewMonth.getFullYear();
                const yearStart = new Date(year, 0, 1);
                const yearEnd = new Date(year, 11, 31);
                const allDisabled =
                  (minDate && yearEnd < minDate) || (maxDate && yearStart > maxDate);
                return (
                  <button
                    key={year}
                    type="button"
                    tabIndex={-1}
                    onClick={() => selectYear(year)}
                    disabled={allDisabled}
                    className={`py-2 text-sm rounded-lg transition-colors ${
                      allDisabled
                        ? 'text-gray-300 cursor-not-allowed'
                        : isCurrent
                        ? 'bg-emerald-600 text-white font-semibold'
                        : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                    }`}
                  >
                    {year}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DatePicker;
